/**
 * Which permission each top-level tab requires.
 *
 * Read by NavTabs (to decide which tabs to render) and by tourRegistry (to
 * decide which tab tours the help menu may offer for tabs the user is not on).
 * Both used to carry their own copy, and tourRegistry's comment admitted as
 * much — "`perm` values mirror the tab gating in NavTabs.jsx". A tab whose
 * permission changed in one file and not the other would either hide a tab
 * while still advertising its tour, or offer a tour that navigates to a tab
 * that is not there.
 *
 * Tabs absent from this map need no permission. Values are ANY-OF lists,
 * matching `can(...perms)` — most tabs name one permission, but Settings is a
 * page of independently gated sections and cannot.
 *
 * Kept in its own dependency-free module deliberately: tourRegistry is imported
 * by a dozen components, and importing NavTabs for this would drag its icon set
 * into all of their chunks. settingsSections.js is dependency-free for the same
 * reason and may be imported here; nothing else may.
 */
import { SETTINGS_TAB_PERMISSIONS } from "./settingsSections";

export const TAB_PERMISSIONS = Object.freeze({
    "tab-messages":   Object.freeze(["messages.send"]),
    "tab-enquiries":  Object.freeze(["enquiries.view"]),
    "tab-payments":   Object.freeze(["payments.view"]),
    "tab-reports":    Object.freeze(["reports.view"]),
    //DERIVED, never written here. Settings used to demand users.manage, which
    //hid the whole page from the default General Manager role — a role granted
    //records.import, whose only route to the import wizard was through this tab.
    //Whatever opens one section opens the tab; see settingsSections.js.
    "tab-settings":   SETTINGS_TAB_PERMISSIONS,
    "tab-recyclebin": Object.freeze(["records.restore"]),
});

export const tabPermission = (tabId) => TAB_PERMISSIONS[tabId];

/**
 * Whether a tab is visible to the holder of `can`.
 *
 * The `!perm || can(perm)` spelling this replaces was copied into NavTabs twice
 * and useGlobalTour once, which is exactly how a list becoming an any-of list
 * turns into two of the three call sites quietly testing an array as a string —
 * `can(["a","b"])` is false forever and hides the tab from everyone.
 */
export function canSeeTab(tabId, can) {
    const perms = TAB_PERMISSIONS[tabId];
    return !perms || perms.length === 0 || can(...perms);
}
